import { useState } from 'react'
import PropTypes from 'prop-types'
import {
  FaCalendarAlt,
  FaDownload,
  FaPrint,
  FaShareAlt,
  FaTasks,
  FaTimes,
  FaUserFriends,
} from 'react-icons/fa'
import { useViewMode } from '../../hooks/useViewMode.jsx'
import { useTranslation } from '../../utils/translations'
import { useLanguage } from '../../context/LanguageContext'
import { exportFamilyPdf, printFamilyPdf } from '../../utils/shareActions'
import './FamilyToolbar.css'

/* On-page toolbar for the family view: the gear's view modes and export
   actions, made visible. The gear keeps working unchanged — this is an
   additive, more discoverable path to the same functions. Share opens a
   progressive-disclosure sheet (dialog on desktop, bottom sheet on mobile)
   with room for more options later. */
const FamilyToolbar = ({ data }) => {
  const { viewMode, setViewMode } = useViewMode()
  const { language } = useLanguage()
  const { t } = useTranslation()
  const [shareOpen, setShareOpen] = useState(false)
  const [busy, setBusy] = useState(null)

  const modes = [
    { key: 'student', label: t('main.studentView'), icon: FaUserFriends },
    { key: 'activity', label: t('main.activityView'), icon: FaTasks },
    { key: 'date', label: t('main.dateView'), icon: FaCalendarAlt },
  ]

  const runShare = async (kind) => {
    setBusy(kind)
    try {
      if (kind === 'pdf') await exportFamilyPdf(data, viewMode, language)
      if (kind === 'print') await printFamilyPdf(data, viewMode, language)
    } finally {
      setBusy(null)
      setShareOpen(false)
    }
  }

  return (
    <>
      <div className="ft-toolbar">
        <div className="ft-seg" role="tablist" aria-label={t('main.viewSettings')}>
          {modes.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={viewMode === key}
              aria-label={label}
              title={label}
              className={`ft-seg-btn ${viewMode === key ? 'ft-seg-btn-active' : ''}`}
              onClick={() => setViewMode(key)}
            >
              <Icon aria-hidden="true" />
              <span className="ft-seg-label">{label}</span>
            </button>
          ))}
        </div>
        <button type="button" className="ft-share-btn" onClick={() => setShareOpen(true)}>
          <FaShareAlt aria-hidden="true" /> {t('main.share')}
        </button>
      </div>

      {shareOpen && (
        <>
          <div className="ft-sheet-backdrop" onClick={() => setShareOpen(false)} />
          <div className="ft-sheet" role="dialog" aria-modal="true" aria-label={t('main.exportOptions')}>
            <div className="ft-sheet-head">
              <span className="ft-sheet-title">{t('main.exportOptions')}</span>
              <button
                type="button"
                className="ft-sheet-close"
                aria-label={t('common.close')}
                onClick={() => setShareOpen(false)}
              >
                <FaTimes />
              </button>
            </div>
            <button
              type="button"
              className="ft-sheet-option"
              disabled={busy !== null}
              onClick={() => runShare('pdf')}
            >
              <span className="ft-option-chip">
                <FaDownload />
              </span>
              <span className="ft-option-text">
                <span className="ft-option-label">{t('main.saveCopy')}</span>
                <span className="ft-option-sub">{busy === 'pdf' ? '…' : t('main.saveCopyHint')}</span>
              </span>
            </button>
            <button
              type="button"
              className="ft-sheet-option"
              disabled={busy !== null}
              onClick={() => runShare('print')}
            >
              <span className="ft-option-chip">
                <FaPrint />
              </span>
              <span className="ft-option-text">
                <span className="ft-option-label">{t('main.print')}</span>
                <span className="ft-option-sub">{busy === 'print' ? '…' : t('main.printHint')}</span>
              </span>
            </button>
          </div>
        </>
      )}
    </>
  )
}

FamilyToolbar.propTypes = {
  data: PropTypes.object,
}

export default FamilyToolbar
